/**
 * @fileoverview Contract Utilities
 * @module @monorepo/types/contracts
 *
 * Helper utilities for creating and working with API contracts.
 */

import { z } from 'zod';

// ============================================================================
// Reusable Pagination Schemas
// ============================================================================

/**
 * Pagination Metadata Schema
 *
 * Reusable Zod schema for pagination metadata.
 * This is the single source of truth for pagination structure.
 *
 * **Why use Zod schema instead of just a TypeScript interface?**
 *
 * 1. **Runtime Validation**: Validates response structure at runtime
 * 2. **Type Generation**: Automatically generates TypeScript type
 * 3. **Contract-First**: Schema IS the contract, not just documentation
 * 4. **Consistency**: Same schema used for validation and type checking
 * 5. **Error Prevention**: Catches bugs like negative pages, invalid totals
 *
 * This is NOT overkill - it's a core principle of contract-first design.
 * The schema validates both incoming query params AND outgoing responses.
 */
export const PaginationMetaSchema = z.object({
	/** Current page number (1-based) */
	page: z.number().int().positive(),
	/** Items per page */
	limit: z.number().int().positive(),
	/** Total number of items */
	total: z.number().int().nonnegative(),
	/** Total number of pages */
	totalPages: z.number().int().nonnegative(),
	/** Whether there is a next page */
	hasNext: z.boolean(),
	/** Whether there is a previous page */
	hasPrev: z.boolean(),
});

/**
 * Inferred PaginationMeta type from schema
 * Use this type in services for contract-first pagination
 */
export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;

/**
 * Create a paginated response schema (internal data structure)
 * Used by some legacy contracts - prefer PaginatedResult<T> type for services
 */
export function createPaginatedSchema<T extends z.ZodTypeAny>(dataSchema: T) {
	return z.object({
		data: z.array(dataSchema),
		meta: PaginationMetaSchema,
	});
}

// ============================================================================
// Type Utilities for Date Transformation
// ============================================================================

/**
 * Convert Date fields to ISO string fields in a type
 * This is used to create the proper API response type from DB entity type
 *
 * Example:
 *   type DbEntity = { id: string; createdAt: Date }
 *   type ApiEntity = DateToString<DbEntity> // { id: string; createdAt: string }
 *
 * Handles:
 * - Direct Date fields: Date → string
 * - Optional Date fields: Date | undefined → string | undefined
 * - Nullable Date fields: Date | null → string | null
 * - Nested objects: { nested: { date: Date } } → { nested: { date: string } }
 * - Optional nested objects: { nested?: { date: Date } } → { nested?: { date: string } }
 * - Arrays: Array<{ date: Date }> → Array<{ date: string }>
 */
export type DateToString<T> = {
	[K in keyof T]: T[K] extends Date
		? string
		: T[K] extends Date | null
			? string | null
			: T[K] extends Date | undefined
				? string | undefined
				: T[K] extends Date | null | undefined
					? string | null | undefined
					: T[K] extends (infer U)[]
						? Array<DateToString<U>>
						: T[K] extends object | undefined
							? DateToString<NonNullable<T[K]>> | Extract<T[K], undefined>
							: T[K] extends object | null
								? DateToString<NonNullable<T[K]>> | Extract<T[K], null>
								: T[K] extends object | null | undefined
									? DateToString<NonNullable<T[K]>> | Extract<T[K], null | undefined>
									: T[K] extends object
										? DateToString<T[K]>
										: T[K];
};

// ============================================================================
// API Response Wrappers
// ============================================================================

/**
 * API Success Response Schema
 * { success: true, data: T, timestamp: string }
 *
 * IMPORTANT: timestamp is an ISO 8601 datetime string
 * The ZodSerializerInterceptor will automatically convert Date objects to ISO strings at runtime
 */
export const ApiSuccessResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
	z.object({
		success: z.literal(true),
		data: dataSchema,
		timestamp: z.iso.datetime().describe('ISO 8601 timestamp'),
	});

/**
 * API Paginated Response Schema
 * { success: true, data: T[], meta: PaginationMeta, timestamp: string }
 *
 * IMPORTANT: timestamp is an ISO 8601 datetime string
 * The ZodSerializerInterceptor will automatically convert Date objects to ISO strings at runtime
 */
export const ApiPaginatedResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
	z.object({
		success: z.literal(true),
		data: z.array(dataSchema),
		meta: PaginationMetaSchema,
		timestamp: z.iso.datetime().describe('ISO 8601 timestamp'),
	});

/**
 * API Error Schema
 */
export const ApiErrorSchema = z.object({
	code: z.string(),
	message: z.string(),
	statusCode: z.number().int().positive().max(599).describe('HTTP status code'),
	details: z.record(z.string(), z.unknown()).optional(),
	validationErrors: z
		.array(
			z.object({
				field: z.string(),
				message: z.string(),
				code: z.string().optional(),
			}),
		)
		.optional(),
});

/**
 * API Error Response Schema
 * { success: false, error: ApiError, timestamp: string }
 */
export const ApiErrorResponseSchema = z.object({
	success: z.literal(false),
	error: ApiErrorSchema,
	timestamp: z.iso.datetime().describe('ISO 8601 timestamp'),
});

/**
 * NestJS Error Response Schema
 * Standard NestJS error structure (matches HttpException)
 * Used for @ApiResponse error documentation
 */
export const ErrorResponseSchema = z.object({
	statusCode: z.number().int().positive().describe('HTTP status code'),
	message: z.union([z.string(), z.array(z.string())]).describe('Error message(s)'),
	error: z.string().optional().describe('Error type'),
});

// ============================================================================
// Type Exports
// ============================================================================

export type ApiError = z.infer<typeof ApiErrorSchema>;
export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>;
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;
export type ApiSuccessResponse<T> = {
	success: true;
	data: T;
	timestamp: string;
};
export type ApiPaginatedSuccessResponse<T> = {
	success: true;
	data: T[];
	meta: PaginationMeta;
	timestamp: string;
};

// ============================================================================
// HTTP Methods
// ============================================================================

/**
 * HTTP Methods
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Route Definition
 */
export interface RouteDefinition<
	TBody extends z.ZodTypeAny = z.ZodTypeAny,
	TParams extends z.ZodTypeAny = z.ZodTypeAny,
	TQuery extends z.ZodTypeAny = z.ZodTypeAny,
	TResponse extends z.ZodTypeAny = z.ZodTypeAny,
> {
	readonly method: HttpMethod;
	readonly path: string;
	readonly body?: TBody;
	readonly params?: TParams;
	readonly query?: TQuery;
	readonly response: TResponse;
}

/**
 * API Contract Definition
 */
export interface ApiContract {
	readonly base: string;
	readonly routes: Record<string, RouteDefinition>;
}

/**
 * Create an API contract with type safety
 *
 * @param base - Base path for all routes (e.g., '/users', '/products')
 * @param routes - Route definitions
 * @returns Type-safe API contract
 *
 * @example
 * ```typescript
 * const UserContract = createContract('/users', {
 *   list: {
 *     method: 'GET',
 *     path: '',
 *     query: ListUsersQuerySchema,
 *     response: PaginatedUsersSchema,
 *   },
 *   create: {
 *     method: 'POST',
 *     path: '',
 *     body: CreateUserSchema,
 *     response: UserSchema,
 *   },
 *   getOne: {
 *     method: 'GET',
 *     path: '/:id',
 *     params: UserIdParamSchema,
 *     response: UserSchema,
 *   },
 * });
 * ```
 */
export function createContract<TRoutes extends Record<string, RouteDefinition>>(
	base: string,
	routes: TRoutes,
) {
	return {
		base,
		routes,
	} as const;
}

/**
 * Get full path for a route
 *
 * @param contract - API contract
 * @param routeName - Route name
 * @returns Full path (base + route path)
 *
 * @example
 * ```typescript
 * const path = getRoutePath(UserContract, 'list'); // '/users'
 * const path = getRoutePath(UserContract, 'getOne'); // '/users/:id'
 * ```
 */
export function getRoutePath<T extends ApiContract>(
	contract: T,
	routeName: keyof T['routes'],
): string {
	const route = contract.routes[routeName as string];
	if (!route) {
		throw new Error(`Route "${String(routeName)}" not found in contract`);
	}
	return contract.base + route.path;
}

/**
 * Get all route paths from a contract
 *
 * @param contract - API contract
 * @returns Object with route names as keys and full paths as values
 *
 * @example
 * ```typescript
 * const paths = getRoutePaths(UserContract);
 * // { list: '/users', create: '/users', getOne: '/users/:id' }
 * ```
 */
export function getRoutePaths<T extends ApiContract>(
	contract: T,
): Record<keyof T['routes'], string> {
	const paths: Record<string, string> = {};
	for (const routeName in contract.routes) {
		paths[routeName] = getRoutePath(contract, routeName);
	}
	return paths as Record<keyof T['routes'], string>;
}

/**
 * Replace path parameters with values
 *
 * @param path - Path with parameters (e.g., '/users/:id')
 * @param params - Parameter values (e.g., { id: 123 })
 * @returns Path with replaced parameters (e.g., '/users/123')
 *
 * @example
 * ```typescript
 * const path = replacePath('/users/:id', { id: 123 }); // '/users/123'
 * const path = replacePath('/users/:userId/posts/:postId', { userId: 1, postId: 2 }); // '/users/1/posts/2'
 * ```
 */
export function replacePath(path: string, params: Record<string, string | number>): string {
	let result = path;
	for (const [key, value] of Object.entries(params)) {
		result = result.replace(`:${key}`, String(value));
	}
	return result;
}

/**
 * Extract route constants for use in controllers
 *
 * @param contract - API contract
 * @returns Object with base path and route paths
 *
 * @example
 * ```typescript
 * const ROUTES = extractRoutes(UserContract);
 * // { base: '/users', list: '/users', create: '/users', getOne: '/users/:id' }
 *
 * // Use in controller:
 * @Controller(ROUTES.base)
 * export class UserController {
 *   @Get(ROUTES.list.replace(ROUTES.base, ''))
 *   async list() { ... }
 * }
 * ```
 */
export function extractRoutes<T extends ApiContract>(contract: T) {
	return {
		base: contract.base,
		...getRoutePaths(contract),
	};
}
